import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";
import { originUtilities } from "@/components/ui/radix-utils";
import { transitions } from "@/styles/transitions";
import { cn } from "@/utils/cn";

const DropdownMenu = DropdownMenuPrimitive.Root;
const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;
const DropdownMenuGroup = DropdownMenuPrimitive.Group;
const DropdownMenuPortal = DropdownMenuPrimitive.Portal;
const DropdownMenuSub = DropdownMenuPrimitive.Sub;
const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;

// CVA variant definitions for Dropdown sub-components
const dropdownMenuContentVariants = cva(
  [
    originUtilities.dropdownMenu,
    "z-50 rounded-md border p-1 shadow-md",
    "max-w-[90vw] max-h-[85vh] overflow-auto",
    "bg-white border-gray-200",
    "dark:bg-gray-950 dark:border-gray-800",
    "radix-state-open:animate-in radix-state-closed:animate-out",
    "radix-state-closed:fade-out-0 radix-state-open:fade-in-0",
    "radix-state-closed:zoom-out-95 radix-state-open:zoom-in-95",
    "radix-side-bottom:slide-in-from-top-2",
    "radix-side-left:slide-in-from-right-2",
    "radix-side-right:slide-in-from-left-2",
    "radix-side-top:slide-in-from-bottom-2",
  ],
  {
    defaultVariants: {
      width: "default",
    },
    variants: {
      width: {
        default: "min-w-[8rem]",
        lg: "min-w-[12rem]",
        md: "min-w-[10rem]",
        sm: "min-w-[6rem]",
        xl: "min-w-[16rem]",
      },
    },
  },
);

const dropdownMenuItemVariants = cva(
  [
    "relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none",
    transitions.colors,
    "radix-highlighted:bg-gray-100 radix-highlighted:text-gray-900",
    "radix-disabled:pointer-events-none radix-disabled:opacity-50",
    "dark:radix-highlighted:bg-gray-800 dark:radix-highlighted:text-gray-50",
  ],
  {
    defaultVariants: {
      inset: false,
    },
    variants: {
      inset: {
        false: "",
        true: "pl-8",
      },
    },
  },
);

const dropdownMenuLabelVariants = cva(
  ["px-2 py-1.5 text-sm font-semibold", "text-gray-500 dark:text-gray-400"],
  {
    defaultVariants: {
      inset: false,
    },
    variants: {
      inset: {
        false: "",
        true: "pl-8",
      },
    },
  },
);

const dropdownMenuCheckboxItemVariants = cva([
  "relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none",
  transitions.colors,
  "radix-highlighted:bg-gray-100 radix-highlighted:text-gray-900",
  "radix-disabled:pointer-events-none radix-disabled:opacity-50",
  "dark:radix-highlighted:bg-gray-800 dark:radix-highlighted:text-gray-50",
]);

const dropdownMenuRadioItemVariants = cva([
  "relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none",
  transitions.colors,
  "radix-highlighted:bg-gray-100 radix-highlighted:text-gray-900",
  "radix-disabled:pointer-events-none radix-disabled:opacity-50",
  "dark:radix-highlighted:bg-gray-800 dark:radix-highlighted:text-gray-50",
]);

const dropdownMenuSubContentVariants = cva([
  "z-50 min-w-[8rem] rounded-md border p-1 shadow-lg",
  "max-w-[90vw] max-h-[85vh] overflow-auto",
  "bg-white border-gray-200",
  "dark:bg-gray-950 dark:border-gray-800",
  "radix-state-open:animate-in radix-state-closed:animate-out",
  "radix-state-closed:fade-out-0 radix-state-open:fade-in-0",
  "radix-state-closed:zoom-out-95 radix-state-open:zoom-in-95",
  "radix-side-bottom:slide-in-from-top-2",
  "radix-side-left:slide-in-from-right-2",
  "radix-side-right:slide-in-from-left-2",
  "radix-side-top:slide-in-from-bottom-2",
]);

const dropdownMenuSubTriggerVariants = cva(
  [
    "flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none",
    "radix-highlighted:bg-gray-100 radix-highlighted:text-gray-900 radix-state-open:bg-gray-100 radix-state-open:text-gray-900",
    "dark:radix-highlighted:bg-gray-800 dark:radix-highlighted:text-gray-50 dark:radix-state-open:bg-gray-800 dark:radix-state-open:text-gray-50",
  ],
  {
    defaultVariants: {
      inset: false,
    },
    variants: {
      inset: {
        false: "",
        true: "pl-8",
      },
    },
  },
);

const dropdownMenuSeparatorVariants = cva(
  "-mx-1 my-1 h-px bg-gray-200 dark:bg-gray-700",
);

const dropdownMenuShortcutVariants = cva(
  "ml-auto text-xs tracking-widest opacity-60",
);

const dropdownMenuItemIndicatorVariants = cva(
  "absolute left-2 flex h-3.5 w-3.5 items-center justify-center",
);

const dropdownMenuCheckIconVariants = cva(
  "h-4 w-4 text-theme-primary dark:text-theme-primary",
);

const dropdownMenuRadioIconVariants = cva(
  "h-2 w-2 fill-current text-theme-primary dark:text-theme-primary",
);

const dropdownMenuSubTriggerChevronVariants = cva("ml-auto h-4 w-4");

/**
 * Props for the DropdownMenuContent component
 */
export interface DropdownMenuContentProps
  extends React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>,
    VariantProps<typeof dropdownMenuContentVariants> {
  /** Alignment of the dropdown (start, center, end) */
  align?: "start" | "center" | "end";
  /** Ref to the underlying element. */
  ref?: React.Ref<React.ElementRef<typeof DropdownMenuPrimitive.Content>>;
}

/**
 * The dropdown content component that contains menu items.
 * The content is constrained to 90% viewport width and 85% viewport height with overflow scrolling
 * to prevent it from taking up the entire screen.
 */
const DropdownMenuContent = ({
  className,
  width = "default",
  align = "center",
  sideOffset = 4,
  ref,
  ...props
}: DropdownMenuContentProps) => {
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        align={align}
        className={cn(dropdownMenuContentVariants({ width }), className)}
        ref={ref}
        sideOffset={sideOffset}
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  );
};

interface DropdownMenuItemProps
  extends React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item>,
    VariantProps<typeof dropdownMenuItemVariants> {
  ref?: React.Ref<React.ElementRef<typeof DropdownMenuPrimitive.Item>>;
}

/**
 * The dropdown item component for menu options
 */
const DropdownMenuItem = ({
  className,
  inset = false,
  ref,
  ...props
}: DropdownMenuItemProps) => {
  return (
    <DropdownMenuPrimitive.Item
      className={cn(dropdownMenuItemVariants({ inset }), className)}
      ref={ref}
      {...props}
    />
  );
};

interface DropdownMenuLabelProps
  extends React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label>,
    VariantProps<typeof dropdownMenuLabelVariants> {
  ref?: React.Ref<React.ElementRef<typeof DropdownMenuPrimitive.Label>>;
}

/**
 * Dropdown label component for section headings
 */
const DropdownMenuLabel = ({
  className,
  inset = false,
  ref,
  ...props
}: DropdownMenuLabelProps) => (
  <DropdownMenuPrimitive.Label
    className={cn(dropdownMenuLabelVariants({ inset }), className)}
    ref={ref}
    {...props}
  />
);

interface DropdownMenuCheckboxItemProps
  extends React.ComponentPropsWithoutRef<
    typeof DropdownMenuPrimitive.CheckboxItem
  > {
  ref?: React.Ref<React.ElementRef<typeof DropdownMenuPrimitive.CheckboxItem>>;
}

/**
 * Dropdown checkbox item for multi-select options
 */
const DropdownMenuCheckboxItem = ({
  className,
  children,
  checked,
  ref,
  ...props
}: DropdownMenuCheckboxItemProps) => {
  return (
    <DropdownMenuPrimitive.CheckboxItem
      checked={checked}
      className={cn(dropdownMenuCheckboxItemVariants(), className)}
      ref={ref}
      {...props}
    >
      <span className={dropdownMenuItemIndicatorVariants()}>
        <DropdownMenuPrimitive.ItemIndicator>
          <svg
            className={dropdownMenuCheckIconVariants()}
            fill="none"
            height="16"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            viewBox="0 0 24 24"
            width="16"
            xmlns="http://www.w3.org/2000/svg"
          >
            <title>Check</title>
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.CheckboxItem>
  );
};

interface DropdownMenuRadioItemProps
  extends React.ComponentPropsWithoutRef<
    typeof DropdownMenuPrimitive.RadioItem
  > {
  ref?: React.Ref<React.ElementRef<typeof DropdownMenuPrimitive.RadioItem>>;
}

/**
 * Dropdown radio item for single select options
 */
const DropdownMenuRadioItem = ({
  className,
  children,
  ref,
  ...props
}: DropdownMenuRadioItemProps) => {
  return (
    <DropdownMenuPrimitive.RadioItem
      className={cn(dropdownMenuRadioItemVariants(), className)}
      ref={ref}
      {...props}
    >
      <span className={dropdownMenuItemIndicatorVariants()}>
        <DropdownMenuPrimitive.ItemIndicator>
          <svg
            className={dropdownMenuRadioIconVariants()}
            fill="none"
            height="16"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            viewBox="0 0 24 24"
            width="16"
            xmlns="http://www.w3.org/2000/svg"
          >
            <title>Circle</title>
            <circle cx="12" cy="12" r="10" />
          </svg>
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.RadioItem>
  );
};

interface DropdownMenuSubContentProps
  extends React.ComponentPropsWithoutRef<
    typeof DropdownMenuPrimitive.SubContent
  > {
  ref?: React.Ref<React.ElementRef<typeof DropdownMenuPrimitive.SubContent>>;
}

/**
 * Dropdown submenu component for nested menu items.
 */
const DropdownMenuSubContent = ({
  className,
  ref,
  ...props
}: DropdownMenuSubContentProps) => (
  <DropdownMenuPrimitive.SubContent
    className={cn(dropdownMenuSubContentVariants(), className)}
    ref={ref}
    {...props}
  />
);

interface DropdownMenuSubTriggerProps
  extends React.ComponentPropsWithoutRef<
      typeof DropdownMenuPrimitive.SubTrigger
    >,
    VariantProps<typeof dropdownMenuSubTriggerVariants> {
  ref?: React.Ref<React.ElementRef<typeof DropdownMenuPrimitive.SubTrigger>>;
}

/**
 * Dropdown submenu trigger component
 */
const DropdownMenuSubTrigger = ({
  className,
  inset = false,
  children,
  ref,
  ...props
}: DropdownMenuSubTriggerProps) => (
  <DropdownMenuPrimitive.SubTrigger
    className={cn(dropdownMenuSubTriggerVariants({ inset }), className)}
    ref={ref}
    {...props}
  >
    {children}
    <svg
      className={dropdownMenuSubTriggerChevronVariants()}
      fill="none"
      height="16"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width="16"
      xmlns="http://www.w3.org/2000/svg"
    >
      <title>Chevron Right</title>
      <polyline points="9 18 15 12 9 6" />
    </svg>
  </DropdownMenuPrimitive.SubTrigger>
);

interface DropdownMenuSeparatorProps
  extends React.ComponentPropsWithoutRef<
    typeof DropdownMenuPrimitive.Separator
  > {
  ref?: React.Ref<React.ElementRef<typeof DropdownMenuPrimitive.Separator>>;
}

/**
 * Dropdown separator component for visual separation between menu items
 */
const DropdownMenuSeparator = ({
  className,
  ref,
  ...props
}: DropdownMenuSeparatorProps) => (
  <DropdownMenuPrimitive.Separator
    className={cn(dropdownMenuSeparatorVariants(), className)}
    ref={ref}
    {...props}
  />
);

/**
 * Dropdown shortcut display component for keyboard shortcuts
 */
const DropdownMenuShortcut = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => (
  <span className={cn(dropdownMenuShortcutVariants(), className)} {...props} />
);

export {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
};
